import { AxiosError } from 'axios';
import { useErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { useNotificationStore } from '@/store/notificationStore';
import type { ErrorType } from '@/types/errors';

type ErrorHandlingCallbacks = {
  on400?: () => void;
  on401?: () => void;
  on403?: () => void;
  on404?: () => void;
  on500?: () => void;
};

const handleErrors = (error: unknown, callbacks: ErrorHandlingCallbacks = {}) => {
  if (error instanceof AxiosError) {
    if (error.response?.status === 400) {
      callbacks.on400?.();
      return;
    }

    if (error.response?.status === 401) {
      callbacks.on401?.();
      return;
    }

    if (error.response?.status === 403) {
      callbacks.on403?.();
      return;
    }

    if (error.response?.status === 404) {
      callbacks.on404?.();
      return;
    }

    if (typeof error.response?.status === 'number' && error.response?.status >= 500) {
      callbacks.on500?.();
      return;
    }
  }

  throw error;
};

export const useErrorHandling = () => {
  const notify = useNotificationStore((state) => state.notify);
  const { t } = useTranslation();
  const { showBoundary } = useErrorBoundary();

  const handleErrorsWithCallbacks = (
    error: unknown,
    customCallbacks: ErrorHandlingCallbacks = {},
  ) => {
    const notifyOnServerError = () => {
      notify({
        message: t('errors.server_error'),
        type: 'error',
      });
    };

    const throwError = (errorType: ErrorType) => {
      showBoundary(errorType);
    };

    const callbacks: ErrorHandlingCallbacks = {
      on400: customCallbacks.on400 || (() => showBoundary(new Error())),
      on401:
        customCallbacks.on401 ||
        (() => {
          throwError('unauthorized');
        }),
      on403:
        customCallbacks.on403 ||
        (() => {
          throwError('forbidden');
        }),
      on404:
        customCallbacks.on404 ||
        (() => {
          throwError('not_found');
        }),
      on500: customCallbacks.on500 || notifyOnServerError,
    };

    handleErrors(error, callbacks);
  };

  return handleErrorsWithCallbacks;
};
